/* eslint-disable no-extra-parens */
import Storage from 'react-native-storage';
import { ngDeps } from '../../common/utilities/ngDependencies';
import { transformToCamelCase } from '../../common/utilities/apiHelpers';
import { getQueryParameterByName } from '../utilities/queryParameters';
import { utilities } from '@weatherbug/common';
import { demandManagerInfiniteScrollAdsRequest, demandManagerRequest } from './demandManagerBidRequests';

const { PulseApi } = utilities;

//TODO: Implement analytics adapter for PreBidJS. Example: https://github.com/prebid/Prebid.js/blob/master/modules/adagioAnalyticsAdapter.js

const getAdConfigUri = 'api/config/displayads';
const getAdConfigCacheKey = 'getAdConfig';
const defaultPreBidTimeout = 1000;
const validPriceGranularityValues = ['low', 'medium', 'high', 'auto', 'dense'];

// To support admiral ad blocker validating a recovered user for impression counts
const  getEngaged = (admiralKey) => {
    try {
        const storage = localStorage.getItem(admiralKey);
        return storage && storage.split(',')[4] > 0;
    } catch (e) {
        console.error(e);
    }
    return false;
};

export class DisplayAdProvider {
    constructor(options = { timeout: 5000 }) {
        this.options = options;
        this.infiniteScrollAdServerRequestSent = false;

        if(!window.storage) {
            window.storage  = new Storage({
                // maximum capacity, default 1000
                size: 1000,
                // Use AsyncStorage for RN, or window.localStorage for web. If not set, data would be lost after reload.
                storageBackend: window.localStorage,
                // expire time, default 30 minutes
                defaultExpires: 1000 * 900,
                // cache data in the memory. default is true.
                enableCache: true
            });
        }
        this.storage = window.storage;
        this.getAdConfigSync = this.getAdConfigSync.bind(this);
        window.storage.sync[getAdConfigCacheKey] = this.getAdConfigSync;
    }

    async getAdConfigSync(params) {
        const { syncParams } = params;
        const { options, pulseApi, hmac, key } = syncParams;
        const { resolve, reject } = params;
        const  apiParams = {};
        try {
            const response = await pulseApi.getData(getAdConfigUri, apiParams, options.timeout, 'json', hmac);
            if (!response) {
                reject('Invalid response');
            }

            transformToCamelCase(response);

            response.responseTime = Date.now();

            window.storage.save({
                key,
                data: response
            });

            resolve(response);
        } catch(error) {
            reject(error);
        }
    }

    async getAdConfig(authId, secret) {
        if (!this.pulseApi) {
            this.pulseApi = new PulseApi(
                authId,
                secret,
                null,
                this.options.timeout
            );
        }

        let options = this.options;

        return await this.storage.load({
            key: getAdConfigCacheKey,
            autoSync: true,
            syncInBackground: false,
            syncParams: {
                pulseApi: this.pulseApi,
                options,
                hmac: true,
                key: getAdConfigCacheKey
            }
        });
    }

    async configureAds(globalAdConfig, adConfig, complianceRequirements)
    {
        return new Promise(resolve =>
        {
            const config = {
                debug: true
            };

            window?.pbjs?.que?.push?.(() => {
                const targetingParameters = adConfig?.keywords ?? {};
                if (globalAdConfig.enableCMPFeatureConfigs) {
                    window?.pbjs?.setConfig({
                        consentManagement: {
                            gpp: {
                                cmpApi: 'iab',
                                timeout: 8000,
                                allowAuctionWithoutConsent: false
                            }
                        }
                    }); 
                } else if (targetingParameters.us_privacy && !globalAdConfig.enableCMPFeatureConfigs) {
                    window?.pbjs?.setConfig({
                        consentManagement: {
                            usp: {
                                cmpApi: 'static',
                                consentData: {
                                    getUSPData: {
                                        uspString: targetingParameters.us_privacy?.[0]
                                    }
                                }
                            }
                        }
                    });   
                } else if (!targetingParameters.us_privacy 
                    && (complianceRequirements?.dataPrivacyEnabled 
                        || complianceRequirements?.sensitiveDataRestrictionEnabled)) {
                    window?.pbjs?.setConfig({
                        consentManagement: {
                            usp: {
                                cmpApi: 'static',
                                timeout: 100,
                                consentData: {
                                    getUSPData: {
                                        uspString: '1YNN'
                                    }
                                }
                            }
                        }
                    });   
                } else {
                    window?.pbjs?.setConfig({
                        consentManagement: {
                            usp: {
                                cmpApi: 'static',
                                timeout: 100,
                                consentData: {
                                    getUSPData: {
                                        uspString: '1--N'
                                    }
                                }
                            }
                        }
                    });   
                }

                // set Bidder-level first party data (in-house custom ad targeting params) on page.
                if (globalAdConfig.enableBidderLevelFirstPartyData 
                    && globalAdConfig.firstPartyDataSupportedBidders?.length 
                    && Array.isArray(globalAdConfig.firstPartyDataSupportedBidders)
                    && adConfig.keywords
                    && typeof adConfig?.keywords === 'object'
                    && Object.keys(adConfig?.keywords || {})?.length > 0) {
                    window?.pbjs?.setBidderConfig?.({
                        // specify which bidders can access the first party data
                        bidders: globalAdConfig.firstPartyDataSupportedBidders,
                        config: {
                            ortb2: {
                                site: {
                                    ext: {
                                        data: { // Inventory data sent to bidders listed above
                                            ...adConfig?.keywords
                                        }
                                    }
                                }
                            }
                        }
                    });
                }
                
                // get the existing rubicon config object
                let config = window?.pbjs?.getConfig?.('rubicon');

                /**
                 * For publishers who are not using GAM as their ad server, 
                 * it will be necessary to update Magnite’s Prebid Analytics (PBA) batch timeout on page load, 
                 * so that the analytics events are not waiting until GPT slot renders before firing.
                 */
                
                if (config) {
                    // disable SRA
                    config.singleRequest = false;   

                    /**
                     * For publishers requesting bids for many ad units at once, 
                     * it may be desirable to disable Single Request Architecture (SRA) for 
                     * Magnite’s Rubicon bid adapter to reduce potential timeouts:
                     */
                    // update the waitForGamSlots and analyticsBatchTimeout values
                    config.waitForGamSlots = false;
                    config.analyticsBatchTimeout = 3000;

                    // update the rubicon config object
                    window?.pbjs?.setConfig?.({
                        rubicon: config
                    });
                }
            });
        
            const forceEnableAdDebugging = getQueryParameterByName('enableAdDebugging');
            if (globalAdConfig.enableDebug || forceEnableAdDebugging)
            {
                const forceEnablePrebidAdDebugging = getQueryParameterByName('enablePrebidAdDebugging');
                if (globalAdConfig.enablePreBid || forceEnablePrebidAdDebugging)
                {
                    window?.apntag?.anq?.push?.(() => {
                        window?.pbjs?.setConfig?.(config);
                        window?.apntag?.enableDebug?.();
                    });
                }
                else
                {
                    window?.apntag?.anq?.push?.(() => {
                        window?.apntag?.enableDebug?.();
                    });
                }
            }

            window?.apntag?.anq?.push?.(() => {
                window?.apntag?.clearRequest?.();
                window?.pbjs?.removeAdUnit?.();

                if (globalAdConfig.endPoint)
                {
                    window?.apntag?.setEndpoint?.(globalAdConfig.endPoint);
                }

                const admiralEngaged =  getEngaged(globalAdConfig.admiralAdBlockerStorageKey);
                const keywords = adConfig.keywords;
                const apnxSetPageKeywords = admiralEngaged ? { ...keywords, 'admiral-engaged': `${admiralEngaged}` } : keywords;
                const consentManagement = globalAdConfig.enableCMPFeatureConfigs 
                    ? { timeout: 5000, tcf : { disabled: true } } // Disables GDPR only since we only want to support GPP at this phase of the implementation -- see: https://learn.microsoft.com/en-us/xandr/seller-tag/set-page-options#disabling-consent-management
                    : { disabled: true, timeout: 10000 };

                let pageOptions = {
                    member: globalAdConfig.memberId,
                    publisherId: globalAdConfig.publisherId,
                    disablePsa: globalAdConfig.disablePsa || true,
                    debug: globalAdConfig.enableDebug || false,
                    enableSafeFrame: globalAdConfig.enableSafeFrame,
                    supplyType: 'web',
                    app: {
                        appid: adConfig.appId
                    },
                    keywords: apnxSetPageKeywords,
                    user: {
                        //age: adConfig.age,
                        gender: adConfig.gender,
                        language: adConfig.language,
                        dnt: adConfig.doNotTrack || false
                    },
                    device: {
                        devTime: Math.round(new Date().getTime() / 1000),
                        useragent: adConfig.useragent,
                        os: adConfig.os,
                        osVersion: adConfig.osVersion,
                        geo: {
                            lat: adConfig.latitude,
                            lng: adConfig.longitude,
                            country: adConfig.country,
                            region: adConfig.region,
                            city: adConfig.city,
                            zip: adConfig.zip,
                            state: adConfig.state
                        }
                    },
                    consentManagement
                };

                if (adConfig?.keywords?.us_privacy) {
                    pageOptions = { ...pageOptions, us_privacy: adConfig?.keywords?.us_privacy?.[0] };
                }

                window?.apntag?.setPageOpts?.(pageOptions);

                if (globalAdConfig.enableSafeFrame) {
                    window?.apntag?.setSafeFrameConfig?.({
                        allowExpansionByPush: globalAdConfig.allowSafeFrameExpansionByPush || false,
                        allowExpansionByOverlay: globalAdConfig.allowSafeFrameExpansionByOverlay || false
                    });
                }

                resolve({ globalAdConfig, adConfig });
            });

        });
    }

    async configurePageSlots(pageId, pageAdConfig, globalAdConfig, latitude, longitude, currentScreenSize, activeRoute, cameraId) {
        return new Promise((resolve, reject) => {
            window?.apntag?.anq?.push?.(() => {
                try {
                    if (pageAdConfig) {
                        // The slotMap property contains the array of slot objects to be sent to Demand Manager; 
                        // if the slot objects in the slotMap are matched to the Ad Unit Patterns within the Demand Manager wrapper, 
                        // the relevant ad units are constructed and sent to the header bidding auction.
                        // So it is safe to use: const adSlot = { name: element.slotId, sizes: [] }; to configure SlotIds on a page.
                        const slotMap = [];
                        const infiniteAdSlotIdExclusionSet = new Set(globalAdConfig.baseInfiniteScrollAdSlotIds);
                        
                        pageAdConfig?.slots?.forEach(element => {
                            if (element.screenSizes.includes(currentScreenSize)) {
                                if (
                                    (activeRoute === 'news' && globalAdConfig.newsDetailTagIds.includes(element.tagId))
                                    || (activeRoute === 'newsstory' && globalAdConfig.newsTagIds.includes(element.tagId))
                                    || (activeRoute === 'weatherlandingcamera' && !cameraId && globalAdConfig.weatherCamDetailTagIds.includes(element.tagId))
                                    || (activeRoute === 'weatherlandingcamera' && cameraId && globalAdConfig.weatherCamTagIds.includes(element.tagId))
                                    || (activeRoute === 'trafficlandingcamera' && !cameraId && globalAdConfig.trafficCamDetailTagIds.includes(element.tagId))
                                    || (activeRoute === 'trafficlandingcamera' && cameraId  && globalAdConfig.trafficCamTagIds.includes(element.tagId))
                                ) {
                                    return;
                                }
                                const adSlot = { 
                                    name: element.slotId, 
                                    sizes: []
                                };
                                element.adProviderConfig.sizes.forEach(function(size) {
                                    adSlot.sizes.push({ w: size[0], h: size[1] });
                                });
                                if (!infiniteAdSlotIdExclusionSet.has(element.slotId)) {
                                    slotMap.push(adSlot);
                                    this.defineAdSlotTag(element, globalAdConfig);   
                                }
                            }
                        });

                        demandManagerRequest(slotMap, resolve({ pageId, pageAdConfig, latitude, longitude }));
                    } else {
                        reject(`No pageAdConfig supplied for configurePageSlots request on pageId: ${pageId}`);
                    }
                }
                catch (error) {
                    reject(error);
                }
            });
        });
    }

    defineAdSlotTag(element, globalAdConfig, slotId, isAdRefresh = false) {
        const tagDefinition    = {
            ...element.adProviderConfig
        };

        tagDefinition.mediaTypes = {
            banner: {
                sizes: [...element.adProviderConfig.sizes]
            }
        };
        tagDefinition.targetId = slotId || element.slotId;
        tagDefinition.tagId    = element.tagId;

        if (tagDefinition.enableSafeFrame === undefined) {
            tagDefinition.enableSafeFrame = globalAdConfig.defaultEnableIndividualAdSafeFrame;
        }

        if (tagDefinition.enableSafeFrame && !tagDefinition.safeframeConfig) {
            tagDefinition.safeframeConfig = {
                allowExpansionByPush: globalAdConfig.allowSafeFrameExpansionByPush || false,
                allowExpansionByOverlay: globalAdConfig.allowSafeFrameExpansionByOverlay || false
            };
        }
        if (isAdRefresh) {
            const targetId = slotId || element.slotId;
            window?.apntag?.modifyTag?.(targetId, tagDefinition);
        } else {
            window?.apntag?.defineTag?.(tagDefinition);
        }
    }

    async renderDisplayAdForSlot(slotId) {
        window?.apntag?.anq?.push?.(() => {
            window?.apntag?.showTag?.(slotId);
        });
    }

    async clearAdRequest() {
        window?.apntag?.anq?.push?.(() => {
            window?.apntag?.clearRequest?.();
            window?.pbjs?.removeAdUnit?.();
        });
    }

    async refreshAd(slotId, pageId, globalAdConfig, isInfiniteScrollAd, infiniteScrollAdDivId) {
        return await new Promise((resolve, reject) => {
            try {
                const slotMap = [];
                const pageAdConfig = globalAdConfig?.pageAdConfigs?.find(pageConfig => pageConfig?.pageId?.toLowerCase() === pageId?.toLowerCase());
                if (pageAdConfig) {
                    const infiniteAdSlotIdExclusionSet = new Set(globalAdConfig.baseInfiniteScrollAdSlotIds);
                    const adElement = pageAdConfig?.slots?.find(element => element?.slotId === slotId 
                            && ((!infiniteAdSlotIdExclusionSet.has(element?.slotId)) 
                                || (isInfiniteScrollAd && infiniteAdSlotIdExclusionSet.has(element?.slotId))));

                    if (!adElement) {
                        reject(`Ad refresh failed for slotId: ${slotId}. Reason: Invalid SlotId`);
                    }
                    
                    const adSlot = isInfiniteScrollAd && infiniteScrollAdDivId 
                        ? { 
                            name: adElement.slotId, 
                            divId: infiniteScrollAdDivId,  
                            sizes: []
                        }
                        : { 
                            name: adElement.slotId, 
                            sizes: [] 
                        };

                    const registeredSlotId = isInfiniteScrollAd ? infiniteScrollAdDivId : slotId;
                    adElement?.adProviderConfig?.sizes?.forEach(function(size) {
                        adSlot.sizes.push({ w: size[0], h: size[1] });
                    });

                    slotMap.push(adSlot);

                    if (isInfiniteScrollAd) {
                        const adConfig = pageAdConfig?.slots?.find(slot => slot.slotId === slotId);
                        this.defineAdSlotTag(adConfig, globalAdConfig, registeredSlotId, true);
                    } else {
                        this.defineAdSlotTag(adElement, globalAdConfig, slotId, true);
                    }

                    this.adserverRefreshRequestSent = false;
                    this.isAdServerRefreshFailSafe = false;

                    if (globalAdConfig.enablePreBid && !globalAdConfig?.disablePrebidOnPagesWithIds?.includes?.(pageId)) {
                        if (isInfiniteScrollAd) {
                            demandManagerInfiniteScrollAdsRequest(slotMap, registeredSlotId, resolve({ registeredSlotId, pageId }), true);
                        } else {
                            demandManagerRequest(slotMap, resolve({ registeredSlotId, pageId }), true);
                        }
                    } else {
                        window?.apntag?.anq?.push?.(() => {
                            window?.apntag?.refresh?.([registeredSlotId]);
                            resolve({ registeredSlotId, pageId });
                        });
                    }
                } else {
                    reject(`Invalid pageAdConfig supplied for refreshAd request on pageId: ${pageId}`);
                }
                
            } catch (error) {
                reject(error);
            }
        });
    }

    async refreshAdV2(slotId, pageId, globalAdConfig, pageAdConfig, isInfiniteScrollAd, infiniteScrollAdDivId, isFullRefresh) {
        return await new Promise((resolve, reject) => {
            try {
                const slotMap = [];
                // const pageAdConfig = globalAdConfig?.pageAdConfigs?.find(pageConfig => pageConfig?.pageId?.toLowerCase() === pageId?.toLowerCase());
                if (pageAdConfig) {
                    const infiniteAdSlotIdExclusionSet = new Set(globalAdConfig.baseInfiniteScrollAdSlotIds);
                    const adElement = pageAdConfig?.slots?.find(element => element?.slotId === slotId 
                            && ((!infiniteAdSlotIdExclusionSet.has(element?.slotId)) 
                                || (isInfiniteScrollAd && infiniteAdSlotIdExclusionSet.has(element?.slotId))));

                    if (!adElement) {
                        reject(`Ad refresh failed for slotId: ${slotId}. Reason: Invalid SlotId`);
                    }
                    
                    const adSlot = isInfiniteScrollAd && infiniteScrollAdDivId 
                        ? { 
                            name: adElement.slotId, 
                            divId: infiniteScrollAdDivId,  
                            sizes: []
                        }
                        : { 
                            name: adElement.slotId, 
                            sizes: [] 
                        };

                    const registeredSlotId = isInfiniteScrollAd ? infiniteScrollAdDivId : slotId;
                    adElement?.adProviderConfig?.sizes?.forEach(function(size) {
                        adSlot.sizes.push({ w: size[0], h: size[1] });
                    });

                    slotMap.push(adSlot);

                    if (isFullRefresh) {
                        if (isInfiniteScrollAd) {
                            const adConfig = pageAdConfig?.slots?.find(slot => slot.slotId === slotId);
                            this.defineAdSlotTag(adConfig, globalAdConfig, registeredSlotId, true);
                        } else {
                            this.defineAdSlotTag(adElement, globalAdConfig, slotId, true);
                        }
                    }

                    // this.adserverRefreshRequestSent = false;
                    // this.isAdServerRefreshFailSafe = false;

                    if (globalAdConfig.enablePreBid && !globalAdConfig?.disablePrebidOnPagesWithIds?.includes?.(pageId)) {
                        if (isInfiniteScrollAd) {
                            demandManagerInfiniteScrollAdsRequest(slotMap, registeredSlotId, resolve({ registeredSlotId, pageId }), true);
                        } else {
                            demandManagerRequest(slotMap, resolve({ registeredSlotId, pageId }), true);
                        }
                    } else {
                        window?.apntag?.anq?.push?.(() => {
                            window?.apntag?.refresh?.([registeredSlotId]);
                            resolve({ registeredSlotId, pageId });
                        });
                    }
                } else {
                    reject(`Invalid pageAdConfig supplied for refreshAd request on pageId: ${pageId}`);
                }
                
            } catch (error) {
                reject(error);
            }
        });
    }

    displayInfiniteScrollAdAction(slotId, adConfig, globalAdConfig) {
        return new Promise((resolve, reject) => {
            if (!slotId) {
                return;
            }
            window?.apntag?.anq?.push?.(() => {
                try {
                    // The slotMap property contains the array of slot objects to be sent to Demand Manager; 
                    // if the slot objects in the slotMap are matched to the Ad Unit Patterns within the Demand Manager wrapper, 
                    // the relevant ad units are constructed and sent to the header bidding auction.
                    //
                    // IMPORTANT: 
                    // Because these are infinite scroll ads, the adSlot should include the divId property,
                    // This is an optional property: divId (string) which tells Demand Manager 
                    // to use the divId as the Prebid Ad Unit Code instead of the name property; 
                    // this can be useful for situations where we do not have unique slotMap name properties.
                    // - Our infinite scroll ads append an incrementing number each time to the ad slot configured on Magnite's Demand Manager.
                    // The above must be considered because the default behavior for Demand Manager is to match the value of each name i.e const adSlot = { name: adConfig.slotId }
                    // property against the Slot Patterns defined within the Ad Unit Pattern in the wrapper, and use the name as the Prebid Ad Unit Code for bid requests

                    const slotMap = [];
                    const adSlot = { 
                        name: adConfig.slotId, 
                        divId: slotId,  
                        sizes: []
                    };
                    adConfig.adProviderConfig.sizes.forEach(function(size) {
                        adSlot.sizes.push({ w: size[0], h: size[1] });
                    });

                    slotMap.push(adSlot);
                    this.defineAdSlotTag(adConfig, globalAdConfig, slotId);
                    
                    if (globalAdConfig.enablePreBid) {
                        demandManagerInfiniteScrollAdsRequest(slotMap, slotId, resolve(slotId), false);
                    } else {
                        window?.apntag?.loadTags?.(slotId);
                        this.renderDisplayAdForSlot(slotId);
                        resolve(slotId);
                    }
                } catch (error) {
                    reject(error);
                }
            });
        });
    }
}
